<template>
  <div
    :id="block.guid"
    class="group"
    :class="{
      'absolute' : !grid,
      'pointer-events-none': !isEditable,
    }"
    :style="style"
  >
    <div
      v-if="!grid && isEditable && !isResourceBlock"
      class="absolute right-0 p-2 z-[11] opacity-0 group-hover:opacity-100 transition-opacity"
      :style="{ top: (preBuffer || 0) * minuteSize + 'px' }"
      @mousedown="handleMoveMouseDown"
    >
      <o-button
        icon="drag"
        size="xs"
        class=""
        translucent
      />
    </div>

    <component
      :is="type"
      v-if="type"
      :key="hashValue"
      :ref="block.hash"
      :block="block"
      :grid="grid"
      :class="{ 'opacity-50': !isEditable }"
    />

    <o-icon
      v-if="!grid && isEditable"
      icon="resize"
      :size="11"
      class="absolute left-1/2 -translate-x-1/2 py-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity cursor-ns-resize text-gray-700 dark:text-gray-200"
      :style="{ bottom: (postBuffer || 0) * minuteSize + 'px' }"
      @mousedown="handleResizeMouseDown"
    />
  </div>
</template>

<script setup lang="ts">
import objectHash from 'object-hash'

import ReservationBlock from '../blocks/Reservation.vue'
import BreakBlock from '../blocks/Break.vue'

import { EmployeeBreak } from '@/_api/models/employeeBreak'
import { Reservation } from '@/_api/models/reservation'

import { useUIStore } from '~/stores/useUIStore'

const { $auth } = useNuxtApp()
const uiStore = useUIStore()

const modifier = inject('modifier', null) as any
const container = inject('container', null) as any

interface Props {
  block: any
  grid?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  grid: false,
})

const hashValue = ref('')

const minuteSize = computed(() => {
  const value = Number($auth?.info?.minuteSize)
  return Number.isFinite(value) && value > 0 ? value : 2
})
const sectionSize = computed(() => uiStore.schedule.sectionSize)

const isBreakBlock = computed(() => props.block.isBreakBlock)
const isReservationBlock = computed(() => props.block.isReservationBlock)
const isBreakActive = computed(() => modifier?.active.item instanceof EmployeeBreak)
const isReservationActive = computed(() => modifier?.active.item instanceof Reservation)
const isResourceBlock = computed(() => props.block.isResourceBlock)

const type = computed(() => {
  if (isReservationBlock.value) { return ReservationBlock }
  if (isBreakBlock.value) { return BreakBlock }
  return false
})

const duration = computed(() => props.block.duration)

const bufferTimes = computed(() => {
  return props.block.bufferTimes || props.block.relatedBlocks
    ?.find((b: any) => b.bufferTimes && b.reservationService.idReservationService == props.block.reservationService.idReservationService)
    ?.bufferTimes
})

const preBuffer = computed(() => bufferTimes.value ? bufferTimes.value.preBuffer : 0)
const postBuffer = computed(() => bufferTimes.value ? bufferTimes.value.postBuffer : 0)

const isEditable = computed(() => {
  const activeItem = modifier?.active.item
  return !activeItem ||
    (isReservationActive.value && activeItem.idReservation === props.block.reservation?.idReservation) ||
    (isBreakActive.value && activeItem.id === props.block.employeeBreak?.id)
})

const style = computed(() => {
  const { block, grid } = props
  const rect = block.rectForWidth(sectionSize.value)

  return grid
    ? ''
    : {
        top: `${(block.startTime - preBuffer.value) * minuteSize.value}px`,
        height: `${(block.duration + postBuffer.value + preBuffer.value) * minuteSize.value}px`,
        width: rect.width + 'px',
        left: rect.x + 'px',
      }
})

watch(() => props.block, () => {
  const block = props.block
  hashValue.value = objectHash({
    startTime: block.startTime,
    duration: block.duration,
    preBuffer: block.bufferTimes ? block.bufferTimes.preBuffer : 0,
    postBuffer: block.bufferTimes ? block.bufferTimes.postBuffer : 0,
  })
}, { immediate: true, deep: true })

function handleMoveMouseDown() {
  if (modifier) {
    modifier.active.move = props.block
    modifier.active.item = props.block.reservation || props.block.employeeBreak
  }
}

function handleResizeMouseDown() {
  if (modifier) {
    modifier.active.resize = props.block
    modifier.active.item = props.block.reservation || props.block.employeeBreak
  }
}
</script>
